<template>
  <div v-if="open">
    <v-dialog
      scollable
      :max-width="step === 1 ? 1200 : 776"
      :scrim="true"
      v-on:keydown.stop
      v-model="open"
    >
      <v-card class="dialog-card">
        <template v-if="step === 1">
          <div class="dialog-title">
            Want to change plan?
          </div>

          <div class="d-flex align-center justify-center">
            <div class="switcher">
              <v-btn-toggle
                v-model="localSubscriptionType"
                color="#363A40"
                group
                density="compact"
              >
                <v-btn
                  value="yearly" class="pl-6 pr-6"
                  :class="localSubscriptionType === 'yearly' ? 'text-white' : ''"
                >
                  Yearly
                </v-btn>

                <v-btn value="monthly" class="pl-6 pr-6"
                       :class="localSubscriptionType === 'monthly' ? 'text-white' : ''">
                  Monthly
                </v-btn>
              </v-btn-toggle>
            </div>
          </div>

          <div class="plans">
            <plan-card
              :key="plan.value"
              v-for="plan in PLANS()"
              :name="plan.name"
              :description="plan.description"
              :monthlyPrice="plan.monthlyPrice"
              :yearlyPrice="plan.yearlyPrice"
              :type="localSubscriptionType"
              :current-plan-name="selectedPlan.name"
              is-small
              is-preview
              @onSelect="onSelectPlan(plan.value)"
            />
            <plan-card
              v-if="ENTERPRISE_PLAN"
              :name="ENTERPRISE_PLAN.name"
              :description="ENTERPRISE_PLAN.description"
              :current-plan-name="selectedPlan.name"
              is-small
              is-preview
              @onSelect="openContactUs"
            />
          </div>
        </template>
        <template v-if="step === 2">
          <div class="pa-2">
            <div class="dialog-header">
              <div class="dialog-subtitle">
                {{ dialogTitle }}
              </div>
              <span @click="onCancel"><v-icon color="#61676E" class="cursor-pointer">mdi-close</v-icon></span>
            </div>

            <div class="d-flex flex-column ga-8 mb-6">
              <div class="dialog-text-container" v-if="subscriptionAccessType === 'FULL'">
                <div class="dialog-text-title">
                  Current Plan
                </div>
                <div class="dialog-text-description">
                  <template v-if="isTrialing">
                    You're currently on a trial for the {{ selectedPlan?.name }} plan at
                    ${{ selectedSubscriptionType === 'monthly' ? selectedPlan?.monthlyPrice : selectedPlan?.yearlyPrice }}
                    per user per {{ selectedSubscriptionType === 'monthly' ? 'month' : 'year' }} for
                    {{ activeSubscription?.subscriptionDetails?.quantity }} {{
                      activeSubscription?.subscriptionDetails?.quantity === 1 ? 'user' : 'users'
                    }}.
                  </template>
                  <template v-else>
                    You're currently subscribed to the {{ selectedPlan?.name }} plan at
                    ${{ selectedSubscriptionType === 'monthly' ? selectedPlan?.monthlyPrice : selectedPlan?.yearlyPrice }}
                    per user per {{ selectedSubscriptionType === 'monthly' ? 'month' : 'year' }} for
                    {{ activeSubscription?.subscriptionDetails?.quantity }} {{
                      activeSubscription?.subscriptionDetails?.quantity === 1 ? 'user' : 'users'
                    }}.
                  </template>
                </div>
              </div>

              <div class="dialog-text-container">
                <div class="dialog-text-title">
                  Next Payment Date
                </div>
                <div class="dialog-text-description" v-if="switchToPlan?.value === 'basic'">
                  <template v-if="subscriptionAccessType === 'FULL'">
                    <template v-if="isTrialing && isSamePlanDifferentPeriod">
                      Your trial will continue with the new billing period. Your first payment of
                      ${{ calculatedPaymentAmount }}
                      is scheduled for <span class="font-weight-bold">{{ nextBillingDate }}</span> when your trial ends.
                    </template>
                    <template v-else-if="isTrialing">
                      If you confirm the plan change, your first payment of
                      ${{ calculatedPaymentAmount }}
                      is scheduled for <span class="font-weight-bold">{{ nextBillingDate }}</span> when your trial ends.
                    </template>
                    <template v-else-if="selectedPlan.value === 'basic'">
                      If you confirm, your next payment of
                      ${{ calculatedPaymentAmount }}
                      will be <span class="font-weight-bold">today</span> and you will regain access to all Basic
                      features.
                    </template>
                    <template v-else>
                      If you confirm the plan change, your next payment of
                      ${{ calculatedPaymentAmount }}
                      will be scheduled for <span class="font-weight-bold">{{ nextBillingDate }}</span>. You will retain
                      access to all Pro features until that
                      date, after which your subscription will switch to the new plan.
                    </template>
                  </template>
                  <template v-else>
                    <template v-if="selectedPlan.value === 'basic'">
                      If you confirm, your next payment of
                      ${{ calculatedPaymentAmount }}
                      will be <span class="font-weight-bold">today</span> and you will regain access to all Basic
                      features.
                    </template>
                    <template v-else>
                      If you confirm plan change, your next payment of
                      ${{ calculatedPaymentAmount }}
                      will be <span class="font-weight-bold">today</span> and you will get access to all Basic features.
                    </template>
                  </template>
                </div>
                <div class="dialog-text-description" v-else>
                  <template v-if="subscriptionAccessType === 'FULL'">
                    <template v-if="selectedPlan.value === 'basic'">
                      If you confirm plan change, your next payment of
                      ${{ calculatedPaymentAmount }}
                      will be <span class="font-weight-bold">today</span> and you will get access to all Pro features.
                    </template>
                    <template v-else-if="isTrialing && isSamePlanDifferentPeriod">
                      Your trial will continue with the new billing period. Your first payment of
                      ${{ calculatedPaymentAmount }}
                      is scheduled for <span class="font-weight-bold">{{ nextBillingDate }}</span> when your trial ends.
                    </template>
                    <template v-else-if="isTrialing">
                      If you confirm plan change, your first payment of
                      ${{ calculatedPaymentAmount }}
                      is scheduled for <span class="font-weight-bold">{{ nextBillingDate }}</span> when your trial ends, and you will get
                      access to all Pro features.
                    </template>
                    <template v-else>
                      Your next payment of ${{ calculatedPaymentAmount }}
                      is scheduled for <span class="font-weight-bold">{{ nextBillingDate }}</span>. Changing to Pro now
                      means you will get access to all Pro
                      features instantly and be charged the difference now.
                    </template>
                  </template>
                  <template v-else>
                    <template v-if="selectedPlan.value === 'basic'">
                      If you confirm plan change, your next payment of
                      ${{ calculatedPaymentAmount }}
                      will be <span class="font-weight-bold">today</span> and you will get access to all Pro features.
                    </template>
                    <template v-else>
                      If you confirm, your next payment of
                      ${{ calculatedPaymentAmount }}
                      will be <span class="font-weight-bold">today</span> and you will regain access to all Pro
                      features.
                    </template>
                  </template>
                </div>
              </div>

              <div class="dialog-text-container" v-if="switchToPlan?.value !== selectedPlan.value">
                <div class="dialog-text-title">
                  What’s changing?
                </div>
                <div class="dialog-text-description" v-if="switchToPlan?.value === 'basic'">
                  <template v-if="subscriptionAccessType === 'FULL' && switchToPlan?.value !== selectedPlan?.value">
                    <div class="mb-4">Starting {{ nextBillingDate }}, after switching to Basic:</div>

                    <ul class="pl-5">
                      <li class="mb-1">You’ll have access to a maximum of 3 projects (created or invited).</li>
                      <li class="mb-1">Any additional projects will be locked.</li>
                      <li>Users in your plan won’t be able to access more than 3 projects unless you upgrade back to
                        Pro.
                      </li>
                    </ul>
                  </template>
                  <template v-else>
                    <div class="mb-4">As a Basic Plan subscriber:</div>

                    <ul class="pl-5">
                      <li class="mb-1">You’ll have access to a maximum of 3 projects (created or invited).</li>
                      <li class="mb-1">Any additional projects will be locked.</li>
                      <li>Users in your plan won’t be able to access more than 3 projects unless you upgrade back to
                        Pro.
                      </li>
                    </ul>
                  </template>
                </div>
                <div class="dialog-text-description" v-else>
                  <template v-if="subscriptionAccessType === 'FULL' && switchToPlan?.value !== selectedPlan?.value">
                    You will be able to create an unlimited amount of both workflows and projects.
                  </template>
                  <template v-else>
                    As a Pro Plan subscriber, you will be able to create an unlimited amount of both workflows and
                    projects.
                  </template>
                </div>
              </div>
            </div>

            <div class="d-flex justify-space-between align-center">
              <a
                href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ"
                target="_blank" class="dialog-link">Contact support</a>
              <v-btn
                variant="flat"
                size="large"
                @click="proceedToUsers">
                Continue
              </v-btn>
            </div>
          </div>
        </template>
        <template v-if="step === 3">
          <div class="pa-2">
            <div class="dialog-header">
              <div class="dialog-subtitle">Choose Users</div>
              <span @click="onCancel"><v-icon color="#61676E" class="cursor-pointer">mdi-close</v-icon></span>
            </div>
            <p class="dialog-description">
              Here you add who you want to have access to the Continuum Design {{ switchToPlan?.name }} plan
            </p>

            <div class="users-container">
              <p class="users-title">Users ({{ usersCount }}):</p>

              <div class="users-list">
                <div
                  v-for="user in sortedUsers"
                  :key="user.email"
                  class="users-list-item"
                  :class="{isMe: user.email === currentUser.email}"
                >
                  <span class="user-email">{{ user.email }}  <strong
                    v-if="user.email === currentUser.email">(You)</strong></span>
                  <div class="user-actions">
                    <div v-if="user.isPro" class="pro-user-badge">
                      Pro User
                    </div>
                    <div class="remove-user-action" @click="removeUser(user.email)"
                         v-if="user.email !== currentUser.email">
                      <v-icon>mdi-close</v-icon>
                    </div>
                  </div>
                </div>
              </div>

              <div class="add-user-form">
                <div class="mb-4">
                  <add-subscription-user-field
                    :modelValue="emailInputValue"
                    :emails="emails"
                    @update:modelValue="(value) => emailInputValue = value"
                    @updateEmails="updateEmails"
                    :disabled="loadingCreateSubscription || loadingValidateUsersSubscriptions"
                  />
                </div>
                <div class="d-flex justify-end">
                  <v-btn
                    size="large"
                    variant="flat"
                    :disabled="!emailsAreValid"
                    :loading="loadingValidateUsersSubscriptions"
                    @click="addNewUser"
                  >
                    Add {{ emails.length ? emails.length : '' }} new member
                  </v-btn>
                </div>
              </div>

              <div class="plan-badge w-100 mb-10">
                <v-icon>mdi-information-outline</v-icon>
                <span v-if="switchToPlan?.value === 'basic'">
                  Newly added users will receive an email invitation to join your Continuum Design Basic plan.
                  <template
                    v-if="switchToPlan?.value !== selectedPlan?.value">
                    Existing users in your plan will receive an email notifying them that their plan has been downgraded.
                  </template>
                </span>
                <span v-else>
                  Newly added users will receive an email invitation to join your Continuum Design Pro plan.
                  <template
                    v-if="switchToPlan?.value !== selectedPlan?.value">
                    Existing users in your plan will receive an email notifying them that their plan has been upgraded.
                  </template>
                </span>
              </div>

              <div class="d-flex justify-space-between align-center">
                <a
                  href="https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ"
                  target="_blank" class="dialog-link">Contact support</a>
                <div class="d-flex ga-2">
                  <v-btn
                    variant="outlined"
                    size="large"
                    @click="onCancel"
                    :disabled="loadingCreateSubscription || loadingValidateUsersSubscriptions"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    variant="flat"
                    size="large"
                    :disabled="loadingValidateUsersSubscriptions || !!emails.length"
                    :loading="loadingCreateSubscription"
                    @click="changePlan"
                  >
                    Continue
                  </v-btn>
                </div>
              </div>

            </div>
          </div>
        </template>
      </v-card>
    </v-dialog>

    <info-message-dialog
      v-if="infoMessageDialogData"
      :is-open="infoMessageDialogData.isOpen"
      :title="infoMessageDialogData.title"
      :message="infoMessageDialogData.message"
      @close="infoMessageDialogData = null"
    />
  </div>
</template>

<script>
import PlanCard from '@/components/PlanCard.vue'
import { ENTERPRISE_PLAN, getPlanByPriceId, getSubscriptionTypeByPriceId, PLANS, hasCardOnFile } from '@/utils/payments'
import { mapState } from 'vuex'
import format from 'date-fns/format'
import store from '@/store'
import InfoMessageDialog from '@/components/InfoMessageDialog.vue'
import AddSubscriptionUserField from '@/components/AddSubscriptionUserField.vue'
import { validateEmail } from '@/utils/validate'

export default {
  components: { AddSubscriptionUserField, InfoMessageDialog, PlanCard },
  computed: {
    ...mapState({
      currentUser: state => state.user.user,
      subscriptionUsers: state => state.payments.subscriptionUsers,
      activeSubscription: state => state.payments.activeSubscription,
      subscriptionAccessType: state => state.payments.subscriptionAccessType,
      loadingCreateSubscription: state => state.payments.loadingCreateSubscription,
      loadingValidateUsersSubscriptions: state => state.payments.loadingValidateUsersSubscriptions
    }),
    ENTERPRISE_PLAN () {
      return ENTERPRISE_PLAN
    },
    isTrialing () {
      return this.activeSubscription?.subscriptionDetails?.status === 'trialing'
    },
    hasCardOnFile () {
      return hasCardOnFile(this.activeSubscription)
    },
    isSamePlanDifferentPeriod () {
      return this.switchToPlan?.value === this.selectedPlan?.value &&
        this.localSubscriptionType !== this.selectedSubscriptionType
    },
    dialogTitle () {
      if (this.isSamePlanDifferentPeriod) {
        const newPeriod = this.localSubscriptionType === 'yearly' ? 'Yearly' : 'Monthly'
        return `Switching to ${newPeriod}? Here's What Changes`
      }

      return `Switching to ${this.switchToPlan?.name}? Here's What Changes`
    },
    calculatedPaymentAmount () {
      const price = this.localSubscriptionType === 'monthly'
        ? this.switchToPlan?.monthlyPrice
        : this.switchToPlan?.yearlyPrice
      return price * this.subscriptionUsers.length
    },
    selectedPlan: function () {
      if (this.activeSubscription) {
        const priceId = this.activeSubscription?.subscriptionDetails?.price?.id || this.activeSubscription?.lastSubscriptionDetails?.price?.id
        return getPlanByPriceId(priceId, this.activeSubscription)
      }
      return null
    },
    selectedSubscriptionType: function () {
      if (this.activeSubscription) {
        const priceId = this.activeSubscription?.subscriptionDetails?.price?.id || this.activeSubscription?.lastSubscriptionDetails?.price?.id
        return getSubscriptionTypeByPriceId(priceId)
      }
      return null
    },
    nextBillingDate: function () {
      if (this.activeSubscription?.subscriptionDetails?.nextBillingDate) {
        return format(new Date(this.activeSubscription?.subscriptionDetails?.nextBillingDate), 'MMMM dd, yyyy')
      }
      return ''
    },
    usersCount: function () {
      return this.users.length
    },
    emailsAreValid: function () {
      if (this.emails.length > 0) {
        return true
      }

      const emailsArray = Array.from(new Set((this.emailInputValue || '').split(',').map((e) => e.trim())))
      const validEmails = emailsArray.filter((email) => !!validateEmail(email) && !this.emails.includes(email))
      return validEmails.length !== 0
    },
    sortedUsers: function () {
      if (this.users?.length > 0) {
        return [...this.users].sort((a, b) => {
          if (a.email === this.currentUser.email) return -1
          if (b.email === this.currentUser.email) return 1

          return new Date(a.updatedAt) - new Date(b.updatedAt)
        })
      }
      return []
    }
  },
  data () {
    return {
      step: 1,
      open: false,
      switchToPlan: null,
      users: [],
      emails: [],
      emailInputValue: '',
      localSubscriptionType: 'yearly',
      infoMessageDialogData: null
    }
  },
  methods: {
    PLANS () {
      return PLANS || []
    },
    openDialog () {
      this.step = 1
      this.switchToPlan = null
      this.users = [...(this.subscriptionUsers || []).map(u => ({ email: u.email, updatedAt: u.updatedAt }))]
      this.open = true

      if (this.selectedSubscriptionType) {
        this.localSubscriptionType = this.selectedSubscriptionType
      }
    },
    onCancel () {
      if (this.loadingValidateUsersSubscriptions || this.loadingCreateSubscription) {
        return
      }

      this.step = 1
      this.open = false
      this.switchToPlan = null
      this.emails = []
    },
    onSelectPlan (planValue) {
      const priceId = this.activeSubscription?.subscriptionDetails?.price?.id || this.activeSubscription?.lastSubscriptionDetails?.price?.id
      const activePlan = getPlanByPriceId(priceId)
      const activeSubscriptionType = getSubscriptionTypeByPriceId(priceId)
      const scheduledPlan = this.activeSubscription?.scheduleSubscriptionDetails
        ? getPlanByPriceId(this.activeSubscription.scheduleSubscriptionDetails.phases[0].items[0].priceId)
        : null
      const scheduledSubscriptionType = this.activeSubscription?.scheduleSubscriptionDetails
        ? getSubscriptionTypeByPriceId(this.activeSubscription.scheduleSubscriptionDetails.phases[0].items[0].priceId)
        : null
      const isCanceled = !!this.activeSubscription?.subscriptionDetails?.cancelAt

      if (scheduledPlan && scheduledPlan.value === planValue && this.localSubscriptionType === scheduledSubscriptionType) {
        this.infoMessageDialogData = {
          title: 'Already Scheduled',
          message: `You're already scheduled to switch to the ${scheduledPlan.name} ${this.localSubscriptionType} plan on your next billing date. No need to select it again.`,
          isOpen: true
        }
        return
      }

      if (
        this.subscriptionAccessType === 'FULL' &&
        this.selectedPlan.value === planValue &&
        this.localSubscriptionType === activeSubscriptionType &&
        !isCanceled && !scheduledPlan && activePlan.value === planValue
      ) {
        this.open = false
        return
      }

      if (
        this.subscriptionAccessType === 'FULL' &&
        this.selectedPlan.value === planValue &&
        this.localSubscriptionType === activeSubscriptionType
      ) {
        this.step = 3
      } else {
        this.step = 2
      }
      this.switchToPlan = PLANS.find(plan => plan.value === planValue)
    },
    async addNewUser () {
      if (!this.emails.length) {
        return
      }

      const usersToAdd = this.emails.map(email => ({ email }))
      const response = await store.dispatch('payments/validateUsersSubscriptions', { users: usersToAdd })

      if (response.success && response.users.length > 0) {
        const usersWithSubscription = []
        response.users.forEach((user) => {
          if (user.status === 'active') {
            usersWithSubscription.push(user)
          } else {
            const alreadyExists = this.users.findIndex(u => u.email === user.email) !== -1
            if (alreadyExists) {
              return
            }
            const newUser = { email: user.email }
            this.users = [...this.users, newUser]
          }
        })

        if (usersWithSubscription.length > 0) {
          const commaSeparatedEmails = usersWithSubscription.map(user => user.email).join(', ')
          const isSingleUser = usersWithSubscription.length === 1

          const message = isSingleUser
            ? `The user with email ${commaSeparatedEmails} already has an active subscription. You don’t need to add them to your plan. Instead, leave this page and invite them directly to the project you want them to access.`
            : `The users with emails ${commaSeparatedEmails} already have active subscriptions. You don’t need to add them to your plan. Instead, leave this page and invite them directly to the projects you want them to access.`

          this.infoMessageDialogData = {
            message,
            isOpen: true
          }
        }
      }

      this.emailInputValue = ''
      this.emails = []
    },
    removeUser (email) {
      this.users = this.users.filter(user => user.email !== email)
    },
    updateEmails (emails) {
      this.emails = emails
    },
    async proceedToUsers () {
      const hasActiveSubscription = !!this.activeSubscription?.subscriptionDetails
      if (!hasActiveSubscription) {
        const paymentMethod = await store.dispatch('payments/getPaymentMethod')
        if (!paymentMethod?.card?.last4) {
          this.open = false
          store.commit('payments/setPlan', this.switchToPlan)
          store.commit('payments/setSubscriptionType', this.localSubscriptionType)
          store.commit('payments/setUsers', this.users.map((user) => ({ email: user.email })))
          this.$router.push('/checkout')
          return
        }
      }

      this.step = 3
    },
    async changePlan () {
      if (this.isTrialing && !this.hasCardOnFile) {
        this.open = false
        store.commit('snackbar/showMessage', {
          content: 'Add a payment method to change your plan during the trial.',
          timeout: 6000,
          location: 'center center'
        }, { root: true })
        this.$router.push('/subscription/billing/manage-billing')
        return
      }

      const createSubscriptionParams = {
        subscriptionPriceId: this.switchToPlan.prices[this.localSubscriptionType],
        users: this.users.map(user => ({
          email: user.email
        }))
      }
      const createSubscriptionResponse = await store.dispatch('payments/createSubscription', createSubscriptionParams)
      if (createSubscriptionResponse.success) {
        this.open = false
        await store.dispatch('payments/getActiveSubscription')
        await store.dispatch('payments/getPayerUserSubscription')
      } else {
        this.infoMessageDialogData = {
          message: createSubscriptionResponse.message,
          isOpen: true
        }
      }
    },
    openContactUs () {
      window.open('https://webforms.pipedrive.com/f/6NfI0XGDzXIe8nGmpN1ly8pQUw2YUXE6GnPVoWuCyliiWcg13YOiNmQBCeX3PF6nbJ')
    }
  }
}
</script>

<style scoped>
.dialog-card {
  padding: 16px;
}

.dialog-title {
  color: var(--text-dark);
  font-size: 24px;
  line-height: 28px;
  font-weight: 500;
  font-style: normal;
  margin-bottom: 24px;
}

.dialog-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
}

.dialog-subtitle {
  font-size: 20px;
  font-weight: 700;
  margin-bottom: 36px;
  color: var(--text-dark, #1D1F22);
}

.dialog-text-container {
  gap: 8px;
  display: flex;
  flex-direction: column;

  .dialog-text-title {
    font-size: 12px;
    font-weight: 600;
    line-height: 12px;
    color: var(--dark-4, #363A40);
  }

  .dialog-text-description {
    font-size: 16px;
    font-weight: 300;
    line-height: 22.4px;
    color: var(--text-dark);
  }
}

.plans {
  gap: 30px;
  display: flex;
  justify-content: center;
}

.dialog-link {
  font-size: 16px;
  font-weight: 400;
  line-height: 22.4px;
  text-decoration: none;
  color: var(--blue-3, #4285F4);
}

.users-container {
  margin-top: 40px;
}

.users-title {
  font-size: 12px;
  font-weight: 600;
  color: var(--dark-3, #61676E);
  margin-bottom: 24px;
}

.add-user-form {
  margin-bottom: 24px;
}

.users-list {
  display: flex;
  margin-bottom: 24px;
  flex-direction: column;
}

.users-list-item {
  gap: 8px;
  height: 34px;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  align-items: center;
  align-self: stretch;
  padding: 6px 8px 8px 8px;
  justify-content: space-between;
  color: var(--dark-4, #363A40);
  border-bottom: 1px solid var(--light-3, #E5E3E1);

  &:first-child {
    border-top: 1px solid var(--light-3, #E5E3E1);
  }
}

.user-email {
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  color: var(--dark-4, #363A40);
}

.user-actions {
  gap: 8px;
  display: flex;
  align-items: center;
}

.pro-user-badge {
  height: 20px;
  display: flex;
  padding: 0 8px;
  align-items: center;
  border-radius: 14px;
  background: var(--blue-3);
  justify-content: center;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  color: var(--light-1, #FCFCFC);
}

.remove-user-action {
  cursor: pointer;
}

.plan-badge {
  gap: 8px;
  padding: 8px;
  display: flex;
  font-size: 14px;
  font-weight: 400;
  border-radius: 8px;
  align-items: center;
  color: var(--dark-4, #363A40);
  background: var(--light-2, #F5F2F0);
}

.switcher {
  padding: 3px;
  border-radius: 8px;
  margin-bottom: 24px;
  border: 1px solid var(--light-4);
}
</style>
